import { useGetList } from "ra-core";
import { Link } from "react-router";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

import type { ProspectActivity } from "../types";
import { ActivityDoneToggle } from "../activities/ActivityDoneToggle";
import { activityTypeLabel } from "../activities/activityModel";

const formatWhen = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const ProspectActivities = ({ prospectId }: { prospectId: number }) => {
  const { data = [], isPending } = useGetList<ProspectActivity>("activities", {
    filter: { prospect_id: prospectId },
    pagination: { page: 1, perPage: 50 },
    sort: { field: "starts_at", order: "ASC" },
  });

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h6 className="text-sm font-semibold">Activities</h6>
        <Button asChild variant="outline" size="sm">
          <Link to={`/activities/create?prospect_id=${prospectId}`}>
            <Plus className="size-4" /> Add
          </Link>
        </Button>
      </div>
      {isPending ? null : data.length === 0 ? (
        <p className="text-sm text-muted-foreground">No activities yet.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {data.map((activity) => (
            <li
              key={activity.id}
              className="flex items-center gap-2 text-sm py-1"
            >
              <ActivityDoneToggle record={activity} />
              <Link
                to={`/activities/${activity.id}`}
                className={`flex-1 flex justify-between gap-2 hover:underline ${
                  activity.completed_at ? "line-through text-muted-foreground" : ""
                }`}
              >
                <span className="truncate">
                  <span className="text-muted-foreground">
                    {activityTypeLabel(activity.type)}
                  </span>{" "}
                  {activity.title}
                </span>
                <span className="text-muted-foreground shrink-0">
                  {formatWhen(activity.starts_at)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
